/**
 * Supporting pieces for `InspectPopover` (task 4.3, `unified-availability-interaction`).
 * Split out of `inspect-popover.tsx` to keep both files under the file-size and
 * per-function complexity lint budgets.
 */
import * as React from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import type { CellEntry } from '@ooh/contracts';
import { getStatusPrecedence } from './multi-stack-cell-content';
import { isHoldRangeUnderCovered } from './availability-cell-visual';
import {
  AvailabilityInspectPopoverContent,
  AvailabilityStatusBadge,
  type AvailabilityInspectStatus,
} from './availability-inspect-popover-content';
import {
  resolveDeadlineFieldNode,
  type DeadlineFieldLabels,
  type TtlRemaining,
} from '../lib/ttl-countdown';
import {
  InspectQuickActions,
  resolveQuickActionFlags,
  type QuickActionFlags,
  type QuickActionPermissions,
} from './inspect-quick-actions';
import type { ReleaseChoiceContext } from './inspect-quick-action-steps';

export function toInspectStatus(status: CellEntry['status']): AvailabilityInspectStatus | null {
  switch (status) {
    case 'OPTION':
      return 'option';
    case 'SOFT_RESERVATION':
      return 'soft';
    case 'HARD_RESERVATION':
      return 'hard';
    case 'SOLD':
      return 'sold';
    case 'UNAVAILABLE':
      return 'blocked';
    default:
      return null;
  }
}

export function resolveDominantEntry(entries: CellEntry[]): {
  dominant: CellEntry | undefined;
  secondary: CellEntry[];
} {
  const sorted = [...entries].sort(
    (a, b) => getStatusPrecedence(b.status) - getStatusPrecedence(a.status),
  );
  return { dominant: sorted[0], secondary: sorted.slice(1) };
}

// ─── Stacked-cell secondary entries ─────────────────────────────────────────

export function InspectSecondaryEntries({
  entries,
  hasLabelAuthorization,
  testId,
}: {
  entries: CellEntry[];
  hasLabelAuthorization: boolean;
  testId: string;
}) {
  if (entries.length === 0) return null;
  return (
    <div
      className="space-y-1 pt-1 border-t border-border/60"
      data-testid={`${testId}-secondary-entries`}
    >
      {entries.map((entry, index) => {
        const entryStatus = toInspectStatus(entry.status);
        if (!entryStatus) return null;
        return (
          <div
            key={entry.reservationId ?? entry.conflictingOfferId ?? index}
            className="flex items-center gap-1.5 text-xs"
          >
            <AvailabilityStatusBadge status={entryStatus} />
            {hasLabelAuthorization && entry.clientName ? (
              <span className="text-muted-foreground">{entry.clientName}</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ─── Occupied-cell view derivation ──────────────────────────────────────────

function buildOfferNode(dominant: CellEntry, hasLabelAuthorization: boolean): React.ReactNode {
  if (!hasLabelAuthorization || !dominant.offerTitle) return undefined;
  return (
    <a
      href={`/offers/${dominant.conflictingOfferId ?? ''}`}
      className="text-primary underline hover:no-underline"
    >
      {dominant.offerTitle}
    </a>
  );
}

export interface OccupiedCellView {
  status: AvailabilityInspectStatus;
  holder?: string;
  client?: string;
  offerNode?: React.ReactNode;
  showWaitlist: boolean;
  ttlLabel?: ReactNode;
  openInPanelHref: string | null;
  flags: QuickActionFlags;
  releaseChoice?: ReleaseChoiceContext;
}

/**
 * True when the inspected cell's own date range under-covers the reservation's full
 * booking window — one cell against one hold, using the same shared
 * `isHoldRangeUnderCovered` comparison `unified-select-popover.tsx`'s
 * `hasUnderCoveredHold` applies to a multi-cell span (CU-86cbh03pj parity for the
 * INSPECT popover's Anuluj/Wymuś zwolnienie). Missing date data on either side is NOT
 * under-covered — under-coverage can't be determined without it, and the
 * pre-existing whole-release-only behavior is the safe default.
 */
function isReleaseCellUnderCovered(
  cellDateFrom: string | undefined,
  cellDateTo: string | undefined,
  holdDateFrom: string | undefined,
  holdDateTo: string | undefined,
): boolean {
  if (!cellDateFrom || !cellDateTo || !holdDateFrom || !holdDateTo) return false;
  const cellFrom = new Date(`${cellDateFrom}T00:00:00.000Z`);
  const cellTo = new Date(`${cellDateTo}T00:00:00.000Z`);
  const holdFrom = new Date(`${holdDateFrom}T00:00:00.000Z`);
  const holdTo = new Date(`${holdDateTo}T00:00:00.000Z`);
  return isHoldRangeUnderCovered(cellFrom, cellTo, holdFrom, holdTo);
}

/** Pure derivation of every occupied-cell (non-ASK_AVAILABILITY/UNAVAILABLE) render input. */
export function resolveOccupiedCellView(
  dominant: CellEntry,
  status: AvailabilityInspectStatus,
  ttlRemaining: TtlRemaining | null,
  opts: {
    hasLabelAuthorization: boolean;
    queueInfo?: { count: number; myPosition: number | null };
    mediaUnitId: number;
    permissions: QuickActionPermissions;
    deadlineLabels: DeadlineFieldLabels;
    /** The inspected cell's own date range (the calendar period it belongs to) — used
     * only to gate the Anuluj/Wymuś whole-versus-selected-period choice. */
    inspectedCellDateFrom?: string;
    inspectedCellDateTo?: string;
  },
): OccupiedCellView {
  const isActiveHold =
    dominant.status === 'SOFT_RESERVATION' || dominant.status === 'HARD_RESERVATION';

  const underCovered = isReleaseCellUnderCovered(
    opts.inspectedCellDateFrom,
    opts.inspectedCellDateTo,
    dominant.dateFrom,
    dominant.dateTo,
  );

  return {
    status,
    holder: opts.hasLabelAuthorization ? dominant.holderName : undefined,
    client: opts.hasLabelAuthorization ? dominant.clientName : undefined,
    offerNode: buildOfferNode(dominant, opts.hasLabelAuthorization),
    showWaitlist: isActiveHold && opts.queueInfo != null && opts.queueInfo.count > 0,
    ttlLabel: resolveDeadlineFieldNode(
      dominant.status,
      dominant.ttlExpiresAt,
      ttlRemaining,
      opts.deadlineLabels,
    ),
    openInPanelHref:
      dominant.reservationId != null
        ? `/media-units/${opts.mediaUnitId}?tab=reservations&focus=reservation:${dominant.reservationId}`
        : null,
    flags: resolveQuickActionFlags(dominant, opts.permissions),
    releaseChoice:
      underCovered && dominant.reservationId != null
        ? {
            mediaUnitId: opts.mediaUnitId,
            reservationId: dominant.reservationId,
            cellDateFrom: opts.inspectedCellDateFrom as string,
            cellDateTo: opts.inspectedCellDateTo as string,
          }
        : undefined,
  };
}

// ─── Waitlist join/leave affordance ─────────────────────────────────────────

/**
 * Join/leave-queue affordance for the waitlist strip — mirrors the retired
 * `CellQueueBlock` (`availability-cell-popover.tsx`) behavior: a "Dołącz do kolejki"
 * button when the caller isn't queued yet, or the caller's position plus an
 * "Opuść kolejkę" button once they are. `onJoinQueue`/`onLeaveQueue` are optional —
 * when a caller doesn't wire them (e.g. surfaces without a working mutation yet),
 * only the read-only position label renders, matching the pre-existing behavior.
 */
function InspectWaitlistAction({
  testId,
  myPosition,
  onJoinQueue,
  onLeaveQueue,
}: {
  testId: string;
  myPosition: number | null;
  onJoinQueue?: () => void;
  onLeaveQueue?: () => void;
}) {
  const { t } = useTranslation('reservations');

  if (myPosition == null) {
    if (!onJoinQueue) return null;
    return (
      <Button
        size="xs"
        variant="outline"
        data-testid={`${testId}-waitlist-join-btn`}
        onClick={onJoinQueue}
      >
        {t('waitlist.joinBtn')}
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <span data-testid={`${testId}-waitlist-position`} className="text-xs font-medium">
        {t('waitlist.inQueueLabel', { position: myPosition })}
      </span>
      {onLeaveQueue ? (
        <Button
          size="xs"
          variant="ghost"
          data-testid={`${testId}-waitlist-leave-btn`}
          onClick={onLeaveQueue}
        >
          {t('waitlist.leaveBtn')}
        </Button>
      ) : null}
    </div>
  );
}

// ─── Occupied-cell shell wiring ─────────────────────────────────────────────

interface InspectOccupiedCellProps {
  view: OccupiedCellView;
  unitLabel: string;
  dateRangeLabel: string;
  secondary: CellEntry[];
  hasLabelAuthorization: boolean;
  queueInfo?: { count: number; myPosition: number | null };
  onUpgradeHard?: () => void;
  onCloseSale?: () => void;
  onCancel?: () => void;
  onForceRelease?: () => void;
  onJoinQueue?: () => void;
  onLeaveQueue?: () => void;
  /** See `AvailabilityInspectPopoverContentProps.queueSection` (CU-86cb7ewja). */
  queueSection?: React.ReactNode;
  testId: string;
}

/** Renders the shared shell for a plain occupied or stacked cell (not ASK_AVAILABILITY/UNAVAILABLE). */
export function InspectOccupiedCell({
  view,
  unitLabel,
  dateRangeLabel,
  secondary,
  hasLabelAuthorization,
  queueInfo,
  onUpgradeHard,
  onCloseSale,
  onCancel,
  onForceRelease,
  onJoinQueue,
  onLeaveQueue,
  queueSection,
  testId,
}: InspectOccupiedCellProps) {
  return (
    <AvailabilityInspectPopoverContent
      status={view.status}
      unitLabel={unitLabel}
      dateRangeLabel={dateRangeLabel}
      holder={view.holder}
      client={view.client}
      offerNode={view.offerNode}
      ttlLabel={view.ttlLabel}
      queueSection={queueSection}
      waitlistCount={view.showWaitlist ? queueInfo?.count : undefined}
      waitlistAction={
        !queueSection && view.showWaitlist ? (
          <InspectWaitlistAction
            testId={testId}
            myPosition={queueInfo?.myPosition ?? null}
            onJoinQueue={onJoinQueue}
            onLeaveQueue={onLeaveQueue}
          />
        ) : undefined
      }
      secondaryEntries={
        <InspectSecondaryEntries
          entries={secondary}
          hasLabelAuthorization={hasLabelAuthorization}
          testId={testId}
        />
      }
      actions={
        <InspectQuickActions
          testId={testId}
          flags={view.flags}
          openInPanelHref={view.openInPanelHref}
          holder={view.holder}
          unitLabel={unitLabel}
          releaseChoice={view.releaseChoice}
          onUpgradeHard={onUpgradeHard}
          onCloseSale={onCloseSale}
          onCancel={onCancel}
          onForceRelease={onForceRelease}
        />
      }
    />
  );
}
